import { useState } from "react";
import { MessageCircle, Facebook, Twitter, Link as LinkIcon, Instagram, Share2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CANONICAL_URL } from "@/lib/api";
import { generateDiwaliStoryImage, shareOrDownloadStoryImage } from "@/lib/storyImageGenerator";
import { useDiwaliCampaign } from "@/hooks/useDiwaliCampaign";

const EARLY_REVEAL_THRESHOLD = 25000;
const SUPPORTERS_REVEAL_THRESHOLD = 25;
const WHATSAPP_TEXT =
  "This Diwali I sent festive meals to 500+ rescued animals at Animal Matter To Me, and a thank you to the caregivers who look after them. Join me:";

function formatINR(n) {
  return `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
}

function shareUrl(channel) {
  return `${CANONICAL_URL}/diwali?utm_source=share&utm_medium=${channel}&utm_campaign=diwali2026`;
}

function nowIST() {
  return new Date(Date.now() + 5.5 * 3600 * 1000);
}

function getCountdownText() {
  const ist = nowIST();
  const todayUTCms = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate());
  const diwaliUTCms = Date.UTC(2026, 10, 8);
  const daysLeft = Math.round((diwaliUTCms - todayUTCms) / 86400000);
  if (daysLeft <= 0) return "Last day to send your Diwali gift";
  if (daysLeft <= 2) return `Last ${daysLeft} days`;
  return `${daysLeft} days to Diwali`;
}

/**
 * `hero` variant is used only when this component sits on top of the hero
 * photo (homepage) - it responsively switches to light text/borders from
 * the md breakpoint up, matching how the rest of the hero already switches
 * colors at md:. Below md, the hero image is not overlaid (text sits on the
 * normal page background), so it keeps the same dark-on-light look as the
 * `light` variant (used untouched on /diwali).
 */
function ShareRow({ raised, hero }) {
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const nativeShare = async () => {
    try {
      await navigator.share({ title: "Diwali for the whole AMTM family", text: WHATSAPP_TEXT, url: shareUrl("native") });
    } catch (err) {
      if (err?.name !== "AbortError") console.error("Native share failed:", err);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl("copy"));
      toast({ title: "Link copied!" });
    } catch {
      toast({ variant: "destructive", title: "Could not copy link" });
    }
  };

  const handleInstagramStory = async () => {
    setBusy(true);
    try {
      const blob = await generateDiwaliStoryImage(raised, shareUrl("instagram"));
      await shareOrDownloadStoryImage(blob, "amtm-diwali-2026.png", shareUrl("instagram"), toast);
    } catch {
      toast({ variant: "destructive", title: "Could not create the story image" });
    } finally {
      setBusy(false);
    }
  };

  const iconBtnClass = hero
    ? "h-11 w-11 rounded-full border border-amtm-border text-amtm-text hover:border-amtm-teal hover:text-amtm-teal flex items-center justify-center transition-colors md:border-[1.5px] md:border-white md:bg-white/[0.12] md:text-white md:hover:bg-white/[0.24] md:hover:text-white"
    : "h-11 w-11 rounded-full border border-amtm-border flex items-center justify-center text-amtm-text hover:border-amtm-teal hover:text-amtm-teal transition-colors";
  const headingClass = hero ? "text-sm md:text-[15px] text-amtm-textMuted md:text-[#F3E8BC] mb-2.5" : "text-sm text-amtm-textMuted mb-2.5";

  return (
    <div className="mt-4" data-testid="diwali-share-row">
      <p className={headingClass}>Share this with someone who loves animals</p>
      <div className="flex items-center gap-2 flex-wrap">
        {canNativeShare && (
          <button type="button" onClick={nativeShare} data-testid="diwali-share-native" className={iconBtnClass} aria-label="Share">
            <Share2 className="h-4.5 w-4.5" />
          </button>
        )}
        <a href={`https://wa.me/?text=${encodeURIComponent(`${WHATSAPP_TEXT} ${shareUrl("whatsapp")}`)}`} target="_blank" rel="noopener noreferrer" data-testid="diwali-share-whatsapp" className={iconBtnClass} aria-label="WhatsApp">
          <MessageCircle className="h-4.5 w-4.5" />
        </a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl("facebook"))}`} target="_blank" rel="noopener noreferrer" data-testid="diwali-share-facebook" className={iconBtnClass} aria-label="Facebook">
          <Facebook className="h-4.5 w-4.5" />
        </a>
        <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl("x"))}&text=${encodeURIComponent(WHATSAPP_TEXT)}`} target="_blank" rel="noopener noreferrer" data-testid="diwali-share-x" className={iconBtnClass} aria-label="X">
          <Twitter className="h-4.5 w-4.5" />
        </a>
        <button type="button" onClick={copyLink} data-testid="diwali-share-copy" className={iconBtnClass} aria-label="Copy link">
          <LinkIcon className="h-4.5 w-4.5" />
        </button>
        <button type="button" onClick={handleInstagramStory} disabled={busy} data-testid="diwali-share-instagram" className={iconBtnClass} aria-label="Instagram Story">
          {busy ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Instagram className="h-4.5 w-4.5" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Shared Diwali ticker + countdown + share row. Used under the /diwali
 * headline (variant="light", default) and under the homepage Diwali hero
 * sub line (variant="hero"). Self-contained - fetches its own live total
 * and refreshes every 60s.
 */
export default function DiwaliProgress({ className = "", variant = "light" }) {
  const { status } = useDiwaliCampaign();
  if (!status) return null;

  const hero = variant === "hero";
  const { raised = 0, supporters = 0, milestones = [200000, 500000, 1000000], final_goal = 1000000 } = status;
  const showBar = raised >= EARLY_REVEAL_THRESHOLD;
  let nextGoal = final_goal;
  for (const m of milestones) {
    if (raised < m) {
      nextGoal = m;
      break;
    }
  }
  const reached = raised >= final_goal;
  const pct = showBar ? Math.min((raised / nextGoal) * 100, 100) : 0;

  const messageClass = hero
    ? "font-semibold text-base md:text-lg text-amtm-text md:text-white"
    : "text-base font-medium text-amtm-text";
  const trackClass = hero ? "h-3 w-full rounded-full bg-black/10 md:bg-white/25 overflow-hidden" : "h-3 w-full rounded-full bg-black/10 overflow-hidden";
  const supportersClass = hero ? "text-sm text-amtm-textMuted md:text-white/80 mt-1" : "text-sm text-amtm-textMuted mt-1";
  const countdownClass = hero ? "text-sm font-semibold text-amtm-teal md:text-[#FFD85F] mt-1" : "text-sm font-semibold text-amtm-teal mt-1";

  return (
    <div className={className} data-testid="diwali-progress">
      {!showBar ? (
        <p className={messageClass} data-testid="diwali-progress-early-message">
          Be one of the first to give this Diwali
        </p>
      ) : (
        <>
          <div className={trackClass} data-testid="diwali-progress-bar">
            <div className="h-full rounded-full bg-[#FFD85F] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className={`${messageClass} mt-2`} data-testid="diwali-progress-line">
            {reached
              ? "Diwali goal reached, thank you"
              : `${formatINR(raised)} raised \u00B7 Next goal ${formatINR(nextGoal)} \u00B7 Diwali goal ${formatINR(final_goal)}`}
          </p>
        </>
      )}
      {supporters >= SUPPORTERS_REVEAL_THRESHOLD && (
        <p className={supportersClass} data-testid="diwali-progress-supporters">
          {supporters.toLocaleString("en-IN")} supporters
        </p>
      )}
      <p className={countdownClass} data-testid="diwali-progress-countdown">
        {getCountdownText()}
      </p>
      <ShareRow raised={raised} hero={hero} />
    </div>
  );
}
